/**
 * Artikel einer Ausfuehrung zuordnen -- und ein Produktmodell sicher loeschen
 * (Produktmodelle 002A).
 *
 * DIE FRAGE, DIE HIER BEANTWORTET WIRD.
 *
 * „Wo kann ich denn jetzt die Artikelnummern eingeben, die fehlenden?" --
 * Arne, 19.09.2026, an TP154. Die Antwort war: nirgends an dieser Stelle.
 * Eine Ausfuehrung ohne Artikel stand in der Matrix, und nichts fuehrte
 * weiter.
 *
 * Jetzt fuehrt sie weiter, auf zwei Wegen: einen BESTEHENDEN Artikel
 * zuordnen -- mit Pruefung vor dem Klick -- oder einen NEUEN aus der
 * Ausfuehrung heraus anlegen, ueber den bestehenden Produktassistenten. Eine
 * Artikelnummer wird nie als Text an die Ausfuehrung geschrieben; zugeordnet
 * wird immer ein Artikel.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AssignmentCandidatesResponse, AssignmentCheck, DiscardPreview, MetaResponse, ModelDeletePreview,
  ModelOverviewResponse, PropertyValue, UnassignPreview, VariantValueRef,
} from "@homebase/shared";
import { api } from "../lib/api.ts";
import { type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { displayValue } from "./ProductProperties.tsx";
import { valueLabel } from "./ModelVariants.tsx";
import { LoadingState } from "./States.tsx";

/** Warum ein Artikel nicht zuordenbar ist -- woertlich fuer die Sprachpruefung. */
const SPERRE: Record<string, Parameters<I18n["t"]>[0]> = {
  trashed: "mz.block.trashed",
  placeholder: "mz.block.placeholder",
  family_mismatch: "mz.block.family_mismatch",
  category_mismatch: "mz.block.category_mismatch",
  other_model: "mz.block.other_model",
  other_execution: "mz.block.other_execution",
  execution_taken: "mz.block.execution_taken",
  value_conflict: "mz.block.value_conflict",
  value_unclear: "mz.block.value_unclear",
  not_main_product: "mz.block.not_main_product",
};

const STATUS: Record<string, Parameters<I18n["t"]>[0]> = {
  ok: "mz.status.ok", conflict: "mz.status.conflict", missing: "mz.status.missing", unclear: "mz.status.unclear",
};

const ABHAENGIG: Record<string, Parameters<I18n["t"]>[0]> = {
  articles: "mz.dep.articles",
  executions_with_article: "mz.dep.executions_with_article",
  texts: "mz.dep.texts",
  properties: "mz.dep.properties",
  documents: "mz.dep.documents",
  packaging: "mz.dep.packaging",
  requests: "mz.dep.requests",
};

/** Die Werte einer Ausfuehrung als eine lesbare Zeile: „4000 K · 80 Ra · 120°". */
export function ausfuehrungText(values: readonly VariantValueRef[], overview: ModelOverviewResponse | null,
                                i18n: I18n): string {
  return values.map((v) => valueLabel(i18n, v,
    overview?.axes.find((a) => a.dimensionId === v.dimensionId)?.unit ?? null)).join(" · ");
}

/** Die Kontextangaben, mit denen der Produktassistent aus einer Ausfuehrung startet. */
export interface AusfuehrungsKontext {
  readonly modelId: number;
  readonly familyId: number;
  readonly categoryId: number;
  readonly variantId: number;
  readonly values: readonly { readonly dimensionId: number; readonly valueId: number }[];
  readonly label: string;
}

export function ZuordnungDialog({ variantId, overview, meta, i18n, mayCreateArticle, onNeuerArtikel, onDone, onClose }: {
  variantId: number;
  overview: ModelOverviewResponse | null;
  meta: MetaResponse;
  i18n: I18n;
  /** article.write -- nur dann gibt es den zweiten Weg. */
  mayCreateArticle: boolean;
  onNeuerArtikel: (() => void) | null;
  onDone: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [weg, setWeg] = useState<"bestehend" | null>(null);
  const [suche, setSuche] = useState("");
  const [daten, setDaten] = useState<AssignmentCandidatesResponse | null>(null);
  const [gewaehlt, setGewaehlt] = useState<AssignmentCheck | null>(null);
  const [uebernehmen, setUebernehmen] = useState(false);
  /*
   * DER WECHSEL (UI/UX 001).
   *
   * Traegt die Ausfuehrung schon eine SKU, ist das Zuordnen einer anderen
   * kein Zuordnen, sondern ein WECHSEL -- und der ist zwei Entscheidungen:
   * die bisherige Zuordnung loesen UND die neue setzen. Seit 002B kann der
   * Server das in einem Vorgang (`replaceCurrent`); bis hierher hat die
   * Oberflaeche es nicht gesendet, und der Kandidat blieb mit
   * „execution_taken" gesperrt stehen -- eine Sackgasse mit Erklaerung.
   *
   * Jetzt steht die Erklaerung VOR der Bestaetigung: wer wechselt, liest,
   * dass der bisherige Artikel bleibt und nur seine Verbindung geht.
   * Laesst sich die bisherige Zuordnung nicht loesen (Kalkulation,
   * Freigabe, Bestand, Angebot, Zulauf, heutige DTE-Listung), gibt es den
   * Wechsel nicht -- dieselbe Sperre wie beim Loesen, derselbe Wortlaut.
   */
  const [wechselOk, setWechselOk] = useState(false);
  const [bisher, setBisher] = useState<UnassignPreview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const laeuft = useRef(false);

  /* Wer heute auf der Ausfuehrung sitzt -- aus der Uebersicht, die der
     Aufrufer ohnehin geladen hat. */
  const aktuell = overview?.executions.find((e) => e.variantId === variantId)?.article ?? null;
  useEffect(() => {
    if (aktuell === null) { setBisher(null); return; }
    let gilt = true;
    void api.unassignPreview(variantId).then((v) => { if (gilt) setBisher(v); }).catch(() => { if (gilt) setBisher(null); });
    return () => { gilt = false; };
  }, [variantId, aktuell?.id]);
  const wechselGesperrt = bisher !== null && bisher.klasse === "blocked";

  const laden = useCallback(async (q: string) => {
    try { setDaten(await api.executionCandidates(variantId, q)); } catch (e) { setError(e); }
  }, [variantId]);
  /* Der Kopf braucht Modell und Ausfuehrungswerte sofort -- auch bevor ein Weg gewaehlt ist. */
  useEffect(() => { void laden(""); }, [laden]);
  useEffect(() => {
    if (weg !== "bestehend") return;
    const h = window.setTimeout(() => void laden(suche), 250);
    return () => window.clearTimeout(h);
  }, [suche, weg, laden]);

  async function zuordnen(): Promise<void> {
    if (!gewaehlt || laeuft.current) return;
    laeuft.current = true; setBusy(true); setError(null);
    try {
      await api.assignArticleToExecution(variantId, {
        articleId: gewaehlt.article.id, adoptMissing: uebernehmen,
        ...(aktuell !== null ? { replaceCurrent: true } : {}),
      });
      onDone();
    } catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }

  const def = (code: string) => meta.propertyDefinitions.find((d) => d.code === code);
  const ja = (b: boolean) => (b ? t("common.yes") : t("common.no"));
  /* `execution_taken` ist beim Wechsel keine Sperre, sondern der Anlass --
     an seine Stelle tritt die ausdrueckliche Bestaetigung darunter. */
  const sperren = (gewaehlt?.blockers ?? []).filter((b) => !(b === "execution_taken" && aktuell !== null));
  const grund = gewaehlt === null ? null
    : sperren.length > 0 ? sperren.map((b) => t(SPERRE[b] ?? "mz.block.value_conflict")).join(" ")
    : wechselGesperrt ? t("mk.unassignBlocked", {
        list: (bisher?.blockers ?? []).map((b) => t(BINDUNG[b] ?? "mk.bind.calculations")).join(", ") })
    : aktuell !== null && !wechselOk ? t("mz.why.confirmReplace")
    : gewaehlt.adoptable.length > 0 && !uebernehmen ? t("mz.why.adoptMissing")
    : null;

  return (
    <div className="mz" role="dialog" aria-label={t("mz.title")} data-testid="mz-dialog">
      <div className="mz__kopf">
        <h3 className="mz__titel">{t("mz.title")}</h3>
        <button className="btn btn--small" onClick={onClose}>{t("common.close")}</button>
      </div>
      {daten
        ? <p className="mz__ausfuehrung" data-testid="mz-ausfuehrung">
            <strong>{daten.model.code}</strong> · {ausfuehrungText(daten.values, overview, i18n)}
          </p>
        : <LoadingState />}

      {aktuell !== null && (
        <p className="mz__aktuell" data-testid="mz-aktuell">
          {t("mz.currentArticle")} <code>{aktuell.articleNumber}</code>
          {wechselGesperrt && (
            <span className="models__note models__note--strong" data-testid="mz-wechsel-gesperrt">
              {" "}{t("mk.unassignBlocked", {
                list: (bisher?.blockers ?? []).map((b) => t(BINDUNG[b] ?? "mk.bind.calculations")).join(", ") })}
            </span>
          )}
        </p>
      )}

      {daten?.replacesTrashed && (
        <p className="models__note models__note--strong" data-testid="mz-ersetzt">
          {t("mz.replacesTrashed", { number: daten.replacesTrashed.articleNumber })}
        </p>
      )}

      {weg === null && (
        <div className="mz__wege">
          <button className="btn btn--primary mz__bestehend" onClick={() => setWeg("bestehend")}>{t("mz.existing")}</button>
          {mayCreateArticle && onNeuerArtikel && (
            <button className="btn mz__neu" onClick={onNeuerArtikel}>{t("mz.new")}</button>
          )}
          <p className="models__note">{t("mz.waysNote")}</p>
        </div>
      )}

      {weg === "bestehend" && (
        <div className="mz__suche">
          <input className="list__search mz__suchfeld" value={suche} autoFocus
            placeholder={t("mz.searchPlaceholder")} aria-label={t("mz.searchPlaceholder")}
            onChange={(e) => { setSuche(e.target.value); setGewaehlt(null); setUebernehmen(false); }} />
          <p className="models__note">{t("mz.candidatesNote")}</p>
          {daten && daten.items.length === 0 && <p className="models__empty">{t("mz.noCandidates")}</p>}
          {daten && gewaehlt === null && (
            <ul className="mz__liste" data-testid="mz-kandidaten">
              {daten.items.map((k) => (
                <li key={k.article.id}>
                  <button className="mz__kandidat" data-testid="mz-kandidat" data-nr={k.article.articleNumber}
                    onClick={() => { setGewaehlt(k); setUebernehmen(false); setWechselOk(false); }}>
                    <code>{k.article.articleNumber}</code>
                    <span className="mz__kandidatname">{k.article.shortDescription ?? ""}</span>
                    <span className={k.blockers.length > 0 ? "mz__stand mz__stand--gesperrt" : "mz__stand mz__stand--ok"}>
                      {k.blockers.length > 0 ? t("mz.blocked")
                        : k.adoptable.length > 0 ? t("mz.fitsWithAdopt") : t("mz.fits")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {gewaehlt && daten && (
            <div className="mz__vorschau" data-testid="mz-vorschau">
              <dl className="mz__fakten">
                <dt>{t("mz.model")}</dt><dd>{daten.model.code}</dd>
                <dt>{t("mz.execution")}</dt><dd>{ausfuehrungText(daten.values, overview, i18n)}</dd>
                <dt>{t("mz.article")}</dt><dd><code>{gewaehlt.article.articleNumber}</code> {gewaehlt.article.shortDescription ?? ""}</dd>
              </dl>
              <ul className="mz__pruefung" data-testid="mz-pruefung">
                <li>{t("mz.chkFamily")}: <strong>{ja(gewaehlt.familyOk)}</strong></li>
                <li>{t("mz.chkCategory")}: <strong>{ja(gewaehlt.categoryOk)}</strong></li>
                <li>{t("mz.chkValues")}: <strong>{ja(gewaehlt.axes.every((x) => x.status === "ok"))}</strong>
                  {gewaehlt.adoptable.length > 0 && <> ({t("mz.missingValues", { n: gewaehlt.adoptable.length })})</>}</li>
                <li>{t("mz.chkOtherModel")}: <strong>{gewaehlt.otherModel ? `${t("common.yes")} (${gewaehlt.otherModel.code})` : t("common.no")}</strong></li>
                <li>{t("mz.chkOtherExecution")}: <strong>{ja(gewaehlt.otherExecution)}</strong></li>
                <li>{t("mz.chkConflicts")}: <strong>{ja(gewaehlt.blockers.length > 0)}</strong></li>
              </ul>
              <table className="mp__table mz__achsen" data-testid="mz-achsen">
                <thead><tr><th>{t("mp.colProperty")}</th><th>{t("mz.colExecution")}</th><th>{t("mz.colArticle")}</th><th>{t("release.colStatus")}</th></tr></thead>
                <tbody>
                  {gewaehlt.axes.map((x) => {
                    const d = def(x.definition);
                    return (
                      <tr key={x.definition} data-status={x.status}>
                        <th>{i18n.tCode("prop", x.definition, x.definition)}</th>
                        <td>{valueLabel(i18n, x.expected, x.unit)}</td>
                        <td>{x.actual.length === 0 ? "—" : x.actual.map((a) =>
                          d ? displayValue(d, a as unknown as PropertyValue, i18n, false) : (a.code ?? a.num ?? "")).join(" · ")}</td>
                        <td className={`mz__status mz__status--${x.status}`}>{t(STATUS[x.status] ?? "mz.status.conflict")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {aktuell !== null && sperren.length === 0 && !wechselGesperrt && (
                <label className="mz__uebernahme" data-testid="mz-wechselbestaetigung">
                  <input type="checkbox" className="mz__wechselok" checked={wechselOk}
                    onChange={(e) => setWechselOk(e.target.checked)} />
                  <span>
                    {t("mz.confirmReplace", {
                      old: aktuell.articleNumber, neu: gewaehlt.article.articleNumber })}
                    <small className="feld__note">
                      {t("mz.confirmReplaceNote", { old: aktuell.articleNumber })}
                    </small>
                  </span>
                </label>
              )}
              {sperren.length === 0 && gewaehlt.adoptable.length > 0 && (
                <label className="mz__uebernahme">
                  <input type="checkbox" checked={uebernehmen} onChange={(e) => setUebernehmen(e.target.checked)} />
                  {t("mz.adoptMissing", { list: gewaehlt.adoptable.map((c) => i18n.tCode("prop", c, c)).join(", ") })}
                </label>
              )}
              <div className="models__formactions">
                <button className="btn btn--primary mz__zuordnen" disabled={busy || grund !== null}
                  onClick={() => void zuordnen()}>{aktuell === null ? t("mz.assign") : t("mz.replace")}</button>
                <button className="btn" onClick={() => { setGewaehlt(null); setUebernehmen(false); setWechselOk(false); }}>{t("mz.back")}</button>
                {grund !== null && <span className="akte__sperrgrund" role="status" data-testid="mz-sperrgrund">{grund}</span>}
              </div>
            </div>
          )}
        </div>
      )}
      {error !== null && <p className="models__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}

/**
 * Produktmodell loeschen -- nur nach Vorschau, nur wenn nichts Fachliches
 * daran haengt. Ist es nicht loeschbar, gibt es KEINEN aktiven Knopf,
 * sondern die Gruende.
 */
export function LoeschDialog({ modelId, i18n, onDeleted, onClose }: {
  modelId: number; i18n: I18n; onDeleted: () => void; onClose: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [v, setV] = useState<ModelDeletePreview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [verstanden, setVerstanden] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.modelDeletePreview(modelId).then(setV).catch(setError); }, [modelId]);

  async function loeschen(): Promise<void> {
    setBusy(true); setError(null);
    try { await api.deleteModel(modelId); onDeleted(); } catch (e) { setError(e); } finally { setBusy(false); }
  }

  return (
    <div className="mz mz--loeschen" role="dialog" aria-label={t("mz.deleteTitle")} data-testid="mz-loeschen">
      <div className="mz__kopf">
        <h3 className="mz__titel">{t("mz.deleteTitle")}</h3>
        <button className="btn btn--small" onClick={onClose}>{t("common.cancel")}</button>
      </div>
      {!v && error === null && <LoadingState />}
      {v && v.deletable && (<>
        <p><strong>{v.code}</strong></p>
        <p>{t("mz.deleteRemoves")}</p>
        <ul className="mz__liste mz__liste--punkte">
          <li>{t("mz.deleteModel", { code: v.code })}</li>
          {v.removes.executions > 0 && <li>{t("mz.deleteExecutions", { n: v.removes.executions })}</li>}
          {v.removes.axes > 0 && <li>{t("mz.deleteAxes", { n: v.removes.axes, values: v.removes.values })}</li>}
        </ul>
        <p className="models__note models__note--strong">{t("mz.deleteNoArticles")}</p>
        <p className="models__note">{t("mz.deleteHistory", { n: v.historyEntries })}</p>
        <label className="mz__uebernahme">
          <input type="checkbox" checked={verstanden} onChange={(e) => setVerstanden(e.target.checked)} />
          {t("mz.deleteConfirm", { code: v.code })}
        </label>
        <div className="models__formactions">
          <button className="btn mz__endgueltig" disabled={!verstanden || busy} onClick={() => void loeschen()}>
            {t("mz.deleteButton")}</button>
          <button className="btn" onClick={onClose}>{t("common.cancel")}</button>
        </div>
      </>)}
      {v && !v.deletable && (
        <div data-testid="mz-nicht-loeschbar">
          <p>{t("mz.notDeletable", { code: v.code })}</p>
          <ul className="mz__liste mz__liste--punkte">
            {v.blockers.map((b) => <li key={b.kind}>{t(ABHAENGIG[b.kind] ?? "mz.dep.articles", { n: b.count })}</li>)}
          </ul>
          <p className="models__note">{t("mz.archiveLater")}</p>
        </div>
      )}
      {error !== null && <p className="models__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}

// ── Zuordnung loesen und Fehlanlage verwerfen (Produktmodelle 002B) ────────

/** Warum eine Zuordnung nicht geloest werden darf. */
const BINDUNG: Record<string, Parameters<I18n["t"]>[0]> = {
  calculations: "mk.bind.calculations",
  price_releases: "mk.bind.price_releases",
  offers: "mk.bind.offers",
  stock: "mk.bind.stock",
  incoming: "mk.bind.incoming",
  dte_events: "mk.bind.dte_events",
  dte_listed: "mk.bind.dte_listed",
  no_article: "mk.bind.no_article",
};

/**
 * „Artikelzuordnung lösen".
 *
 * Die Vorschau sagt in einem Satz, was passiert -- und vor allem, was NICHT
 * passiert: der Artikel bleibt, seine Nummer bleibt, seine Daten bleiben.
 * Gelöst wird eine Verbindung, nicht ein Produkt.
 */
export function LoesenDialog({ variantId, i18n, onDone, onClose }: {
  variantId: number; i18n: I18n; onDone: () => void; onClose: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [v, setV] = useState<UnassignPreview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const laeuft = useRef(false);
  useEffect(() => { api.unassignPreview(variantId).then(setV).catch(setError); }, [variantId]);

  async function loesen(): Promise<void> {
    if (laeuft.current) return;
    laeuft.current = true; setBusy(true); setError(null);
    try { await api.unassignExecution(variantId); onDone(); }
    catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }

  return (
    <div className="mz" role="dialog" aria-label={t("mk.unassignTitle")} data-testid="mk-loesen">
      <div className="mz__kopf">
        <h3 className="mz__titel">{t("mk.unassignTitle")}</h3>
        <button className="btn btn--small" onClick={onClose}>{t("common.cancel")}</button>
      </div>
      {!v && error === null && <LoadingState />}
      {v && v.article && (<>
        <p><strong>{v.model.code}</strong> · {ausfuehrungText(v.values, null, i18n)}</p>
        <p>{t("mk.unassignText", { number: v.article.articleNumber })}</p>
        <ul className="mz__liste mz__liste--punkte" data-testid="mk-loesen-folgen">
          <li>{t("mk.unassignKeepsArticle")}</li>
          <li>{t("mk.unassignKeepsNumber")}</li>
          <li>{t("mk.unassignFree")}</li>
        </ul>
        {v.klasse === "keep" && (
          <p className="models__note">{t("mk.unassignKeepData", {
            list: v.references.filter((r) => r.count > 0)
              .map((r) => `${i18n.tCode("mkRef", r.kind, r.kind)} ${r.count}`).join(", ") })}</p>
        )}
        {v.klasse === "blocked" && (
          <p className="models__note models__note--strong" data-testid="mk-loesen-gesperrt">
            {t("mk.unassignBlocked", {
              list: v.blockers.map((b) => t(BINDUNG[b] ?? "mk.bind.calculations")).join(", ") })}
          </p>
        )}
        <div className="models__formactions">
          <button className="btn btn--primary mk__loesen" disabled={busy || v.klasse === "blocked"}
            onClick={() => void loesen()}>{t("mk.unassign")}</button>
          <button className="btn" onClick={onClose}>{t("common.cancel")}</button>
        </div>
      </>)}
      {error !== null && <p className="models__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}

/**
 * „Fehlerhafte Anlage verwerfen".
 *
 * Drei bestehende Schritte in einem bewussten Vorgang -- und die Vorschau
 * zeigt sie einzeln, samt allem, was dabei verschwindet. Bestätigt wird mit
 * der Artikelnummer, wie beim endgültigen Löschen.
 */
export function VerwerfenDialog({ articleId, i18n, onDone, onClose }: {
  articleId: number; i18n: I18n; onDone: () => void; onClose: () => void;
}): React.JSX.Element {
  const t = i18n.t;
  const [v, setV] = useState<DiscardPreview | null>(null);
  const [eingabe, setEingabe] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const laeuft = useRef(false);
  useEffect(() => { api.discardPreview(articleId).then(setV).catch(setError); }, [articleId]);

  async function verwerfen(): Promise<void> {
    if (laeuft.current || !v) return;
    laeuft.current = true; setBusy(true); setError(null);
    try { await api.discardFailedCreation(articleId, { confirmNumber: eingabe.trim() }); onDone(); }
    catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }

  return (
    <div className="mz mz--loeschen" role="dialog" aria-label={t("mk.discardTitle")} data-testid="mk-verwerfen">
      <div className="mz__kopf">
        <h3 className="mz__titel">{t("mk.discardTitle")}</h3>
        <button className="btn btn--small" onClick={onClose}>{t("common.cancel")}</button>
      </div>
      {!v && error === null && <LoadingState />}
      {v && (<>
        <dl className="mz__fakten">
          <dt>{t("mz.article")}</dt><dd><code>{v.article.articleNumber}</code> {v.article.shortDescription ?? ""}</dd>
          {v.model && <><dt>{t("mz.model")}</dt><dd>{v.model.code}</dd></>}
          {v.execution && <><dt>{t("mz.execution")}</dt><dd>{ausfuehrungText(v.execution.values, null, i18n)}</dd></>}
        </dl>
        {v.discardable ? (<>
          <p className="models__note">{v.reasons.join(" ")}</p>
          <p>{t("mk.discardSteps")}</p>
          <ol className="mz__liste mz__liste--punkte" data-testid="mk-schritte">
            {v.execution && <li>{t("mk.stepUnassign")}</li>}
            <li>{t("mk.stepTrash")}</li>
            <li>{t("mk.stepPurge")}</li>
            <li>{t("mk.stepNumberFree", { number: v.article.articleNumber })}</li>
          </ol>
          {v.losing.length > 0 && (
            <p className="models__note models__note--strong" data-testid="mk-verlust">
              {t("mk.discardLosing", { list: v.losing.map((x) => `${x.label} ${x.count}`).join(", ") })}
            </p>
          )}
          <label className="models__field mk__bestaetigung">
            <span className="models__fieldlabel">{t("mk.discardConfirmLabel", { number: v.article.articleNumber })}</span>
            <input className="list__search mk__nummer" value={eingabe} autoFocus
              onChange={(e) => setEingabe(e.target.value)} />
          </label>
          <div className="models__formactions">
            <button className="btn mz__endgueltig mk__verwerfen"
              disabled={busy || eingabe.trim() !== v.article.articleNumber}
              onClick={() => void verwerfen()}>{t("mk.discard")}</button>
            <button className="btn" onClick={onClose}>{t("common.cancel")}</button>
          </div>
        </>) : (
          <div data-testid="mk-nicht-verwerfbar">
            <p>{v.reasons.join(" ")}</p>
            {v.history.length > 0 && (
              <ul className="mz__liste mz__liste--punkte">
                {v.history.map((h) => <li key={h.label}>{h.label}: {h.count}</li>)}
              </ul>
            )}
          </div>
        )}
      </>)}
      {error !== null && <p className="models__error" role="alert">{errorText(i18n, error)}</p>}
    </div>
  );
}
